/**
 * AutoCarousel — web component for auto-sliding product carousel.
 * Matches production PlantX "Plant-Based Cleaning" behavior:
 * slides one item at a time, pauses between slides, infinite loop.
 *
 * Attributes:
 *   data-autoplay-speed  – pause between slides in ms (default 3000)
 *   data-speed           – slide transition duration in ms (default 1000)
 *   data-slides-to-show  – visible items on desktop (default 5)
 */
class AutoCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.items = [];
    this.itemWidth = 0;
    this.current = 0;
    this.timer = null;
    this.isHovered = false;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  }

  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.items = Array.from(this.track.children);
    if (this.items.length === 0) return;

    this.autoplaySpeed = parseInt(this.getAttribute('data-autoplay-speed') || '3000', 10);
    this.speed = parseInt(this.getAttribute('data-speed') || '1000', 10);
    this.slidesToShow = parseInt(this.getAttribute('data-slides-to-show') || '5', 10);

    this.track.style.transition = 'transform ' + this.speed + 'ms ease';

    this.cloneItems();
    this.measure();
    this.setupEvents();

    if (!this.prefersReducedMotion.matches) {
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  cloneItems() {
    var originals = Array.from(this.track.children);
    originals.forEach(function (item) {
      var clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      this.track.appendChild(clone);
    }.bind(this));
  }

  measure() {
    var firstItem = this.track.firstElementChild;
    if (!firstItem) return;
    var style = window.getComputedStyle(this.track);
    var gap = parseFloat(style.gap) || parseFloat(style.columnGap) || 0;
    this.itemWidth = firstItem.offsetWidth + gap;
  }

  setupEvents() {
    this.addEventListener('mouseenter', function () {
      this.isHovered = true;
      this.stopAutoplay();
    }.bind(this));

    this.addEventListener('mouseleave', function () {
      this.isHovered = false;
      this.startAutoplay();
    }.bind(this));

    window.addEventListener('resize', this.handleResize.bind(this));

    this.prefersReducedMotion.addEventListener('change', function () {
      if (this.prefersReducedMotion.matches) {
        this.stopAutoplay();
      } else if (!this.isHovered) {
        this.startAutoplay();
      }
    }.bind(this));
  }

  handleResize() {
    this.measure();
    this.track.style.transition = 'none';
    this.track.style.transform = 'translateX(-' + (this.current * this.itemWidth) + 'px)';
    // Force reflow then restore transition
    this.track.offsetHeight;
    this.track.style.transition = 'transform ' + this.speed + 'ms ease';
  }

  startAutoplay() {
    if (this.timer) return;
    this.timer = setInterval(this.next.bind(this), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  next() {
    this.current++;
    this.track.style.transform = 'translateX(-' + (this.current * this.itemWidth) + 'px)';

    // When we've scrolled past all original items, snap back seamlessly
    if (this.current >= this.items.length) {
      var self = this;
      setTimeout(function () {
        self.track.style.transition = 'none';
        self.current = 0;
        self.track.style.transform = 'translateX(0)';
        // Force reflow then restore transition
        self.track.offsetHeight;
        self.track.style.transition = 'transform ' + self.speed + 'ms ease';
      }, self.speed);
    }
  }
}

customElements.define('auto-carousel', AutoCarousel);
